import { ListPublicationsResponse, Publication } from '@amzn/awscat-pattern-library-service-model';

import JSONSerializer from '../common/JSONSerializer';

/** NDJSON text cut at the last line break: the full lines, plus the unfinished tail. */
interface NdjsonSplit {
  readonly lines: readonly string[];
  readonly rest: string;
}

/** Splits NDJSON text into complete lines, keeping the unfinished tail for the next chunk. */
export function splitNdjsonLines(text: string): NdjsonSplit {
  const pieces = text.split('\n');
  const rest = pieces.pop() ?? '';
  return { lines: pieces, rest };
}

/** Checks whether a download link points to an NDJSON file rather than a single JSON file. */
export function isNdjsonUrl(url: string): boolean {
  return new URL(url).pathname.endsWith('.ndjson');
}

/**
 * Downloads the NDJSON publication list at `url` and yields the publications each network chunk
 * completes, so callers can show them before the download ends.
 * @throws {Error} when the download fails or a line isn't a valid publication.
 */
export async function* readPublicationBatches(
  url: string,
  serializer: JSONSerializer,
): AsyncGenerator<ListPublicationsResponse> {
  const response = await fetch(url);
  if (!response.ok || !response.body) {
    throw new Error(`Downloading publications failed with HTTP ${response.status}.`);
  }

  // Blank lines carry no publication, so skip them instead of failing the parse.
  function parsePublications(lines: readonly string[]) {
    return lines
      .filter((line) => line.trim())
      .map((line) => serializer.deserialize(line, Publication));
  }

  // A chunk can end mid-line, so hold the tail until the next chunk completes it.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let pending = '';
  let isDone = false;
  try {
    for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
      const { lines, rest } = splitNdjsonLines(
        pending + decoder.decode(chunk.value, { stream: true }),
      );
      pending = rest;
      const publications = parsePublications(lines);
      if (publications.length) {
        yield ListPublicationsResponse.create(publications);
      }
    }
    isDone = true;
  } finally {
    // Stop the download when the caller quits early; a failed cancel leaves nothing more to clean up.
    if (!isDone) {
      reader.cancel().catch(() => undefined);
    }
  }

  // The file may end without a trailing line break.
  const lastPublications = parsePublications([pending + decoder.decode()]);
  if (lastPublications.length) {
    yield ListPublicationsResponse.create(lastPublications);
  }
}

/** Waits for every batch and returns all publications in one list, for callers that need the full set. */
export async function readAllPublications(
  batches: AsyncIterable<ListPublicationsResponse>,
): Promise<Publication[]> {
  const publications: Publication[] = [];
  for await (const batch of batches) {
    publications.push(...batch.publications);
  }
  return publications;
}
